import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, PLAN_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
    const { user, loadUser } = useAuth();
    const [company, setCompany] = useState(null);
    const [form, setForm] = useState({ name: "", address: "", phone: "" });

    const load = async () => {
        const { data } = await api.get("/company");
        setCompany(data);
        setForm({ name: data.name || "", address: data.address || "", phone: data.phone || "" });
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            await api.patch("/company", form);
            toast.success("保存しました");
            await load();
            await loadUser();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const changePlan = async (plan) => {
        if (!window.confirm(`プランを ${PLAN_LABELS[plan]} に変更しますか？`)) return;
        try {
            await api.patch("/company", { plan });
            toast.success(`${PLAN_LABELS[plan]} プランに変更しました`);
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (!company) return <p data-testid="settings-loading">読込中…</p>;

    const plans = [
        { v: "starter", price: "9,800", limit: "50台まで" },
        { v: "business", price: "49,800", limit: "300台まで" },
        { v: "enterprise", price: "要相談", limit: "無制限" },
    ];

    return (
        <div className="space-y-5" data-testid="settings-page">
            <div>
                <p className="prc-label-overline">会社・プラン</p>
                <h1 className="text-[1.685rem] font-bold tracking-tight">設定</h1>
            </div>

            <div className="prc-card p-6">
                <p className="prc-label-overline mb-3">会社情報</p>
                <div className="grid md:grid-cols-2 gap-3">
                    <F label="会社名" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="co-name" />
                    <F label="電話" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="co-phone" />
                    <div className="md:col-span-2">
                        <F label="住所" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="co-address" />
                    </div>
                </div>
                <div className="flex justify-end mt-4">
                    <button onClick={save} className="prc-btn-primary" data-testid="co-save">保存</button>
                </div>
            </div>

            <div>
                <p className="prc-label-overline mb-3">契約プラン</p>
                <div className="grid md:grid-cols-3 gap-4">
                    {plans.map((p) => {
                        const active = company.plan === p.v;
                        return (
                            <div key={p.v} className={`prc-card p-5 ${active ? "ring-2 ring-[var(--prc-ink)]" : ""}`} data-testid={`plan-${p.v}`}>
                                <div className="flex items-center justify-between">
                                    <p className="prc-label-overline">{PLAN_LABELS[p.v]}</p>
                                    {active && <span className="prc-badge ok inline-flex items-center gap-1"><CheckCircle size={16} />現行</span>}
                                </div>
                                <p className="mt-3 text-3xl font-mono font-bold">¥{p.price}<span className="text-sm font-normal text-[var(--prc-muted)] font-sans ml-1">/月</span></p>
                                <p className="mt-1 text-sm text-[var(--prc-muted)]">{p.limit}</p>
                                {!active && (
                                    <button onClick={() => changePlan(p.v)} className="mt-4 prc-btn-ghost w-full" data-testid={`plan-change-${p.v}`}>
                                        このプランに変更
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="prc-card p-6">
                <p className="prc-label-overline mb-3">アカウント情報</p>
                <dl className="text-sm space-y-2">
                    <Row k="ログインメール" v={user?.email} />
                    <Row k="担当者氏名" v={user?.name} />
                    <Row k="権限" v={user?.role} />
                    <Row k="会社ID" v={user?.company_id} mono />
                </dl>
            </div>

            <OperationKindsCard />

            <TaxonomyCard category="operation_location" title="運行地点マスタ（出発地・到着地）" hint="運行記録ダイアログの「出発地」「到着地」プルダウンに表示される選択肢を管理します。本社・営業所・取引先など、よく使う地点を登録してください。" />
            <TaxonomyCard category="maintenance_kind" title="整備種別マスタ" hint="整備記録ダイアログの「種別」ドロップダウンに表示される選択肢を管理します。" />
            <TaxonomyCard category="inspection_issue" title="点検不備カテゴリマスタ" hint="使用前点検の不備分類で使う候補を管理します（将来の集計レポート用）。" />
            <TaxonomyCard category="driver_role" title="運転者役職マスタ" hint="ユーザー登録時に役職を分類するための選択肢を管理します。" />

            {/* White Label — Super Admin（運営）のみ表示 */}
            {user?.role === "super_admin" && (
                <WhiteLabelCard company={company} onSaved={load} />
            )}
        </div>
    );
}

/**
 * Generic taxonomy editor — drives any category exposed by /api/taxonomies.
 * Reuses OperationKindsCard's UI conventions (color picker, code/label/order,
 * inline edit, system-protected delete).
 */
function TaxonomyCard({ category, title, hint }) {
    const [items, setItems] = useState([]);
    const [newItem, setNewItem] = useState({ value: "", label: "", color: "#38bdf8", sort_order: 50 });
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState({});

    const load = async () => {
        try {
            const { data } = await api.get(`/taxonomies/${category}`);
            setItems(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "読込失敗"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [category]);

    const create = async () => {
        const v = newItem.value.trim().toLowerCase();
        const l = newItem.label.trim();
        if (!v || !l) { toast.error("コードと表示名を入力してください"); return; }
        if (!/^[a-z0-9_]+$/.test(v)) { toast.error("コードは英小文字・数字・アンダースコアのみ使用可能です"); return; }
        try {
            await api.post(`/taxonomies/${category}`, { ...newItem, value: v, label: l });
            toast.success("追加しました");
            setNewItem({ value: "", label: "", color: "#38bdf8", sort_order: 50 });
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "追加失敗"); }
    };

    const beginEdit = (it) => {
        setEditingId(it.id);
        setEditDraft({ label: it.label, color: it.color || "", sort_order: it.sort_order, enabled: it.enabled });
    };
    const saveEdit = async (id) => {
        try {
            await api.patch(`/taxonomies/${category}/${id}`, editDraft);
            toast.success("更新しました");
            setEditingId(null);
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "更新失敗"); }
    };
    const remove = async (it) => {
        if (!window.confirm(`「${it.label}」を削除しますか？`)) return;
        try {
            await api.delete(`/taxonomies/${category}/${it.id}`);
            toast.success("削除しました");
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "削除失敗"); }
    };
    const toggleEnabled = async (it) => {
        try {
            await api.patch(`/taxonomies/${category}/${it.id}`, { enabled: !it.enabled });
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "更新失敗"); }
    };

    return (
        <div className="prc-card p-6" data-testid={`taxonomy-card-${category}`}>
            <p className="prc-label-overline mb-3">{title}</p>
            {hint && <p className="text-xs text-[var(--prc-muted)] mb-3">{hint}</p>}

            <div className="overflow-x-auto">
                <table className="prc-table text-sm" data-testid={`taxonomy-table-${category}`}>
                    <thead>
                        <tr>
                            <th className="text-left w-12">色</th>
                            <th className="text-left">コード</th>
                            <th className="text-left">表示名</th>
                            <th className="text-right">並び順</th>
                            <th className="text-center">有効</th>
                            <th className="text-right">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it) => (
                            <tr key={it.id} data-testid={`tx-row-${category}-${it.value}`} className={it.enabled ? "" : "opacity-50"}>
                                {editingId === it.id ? (
                                    <>
                                        <td><input type="color" className="w-8 h-8 rounded-sm border border-[var(--prc-border)] bg-transparent" value={editDraft.color || "#888888"} onChange={(e) => setEditDraft({ ...editDraft, color: e.target.value })} /></td>
                                        <td className="font-mono text-xs">{it.value}{it.system && <span className="ml-2 inline-block px-1 py-0 border border-[var(--prc-border)] rounded-sm text-[9px] font-mono uppercase text-[var(--prc-muted)]">system</span>}</td>
                                        <td><input className="prc-input !py-1" value={editDraft.label} onChange={(e) => setEditDraft({ ...editDraft, label: e.target.value })} /></td>
                                        <td className="num"><input type="number" className="prc-input !py-1 !w-20 text-right" value={editDraft.sort_order ?? 0} onChange={(e) => setEditDraft({ ...editDraft, sort_order: parseInt(e.target.value) || 0 })} /></td>
                                        <td className="text-center"><input type="checkbox" checked={editDraft.enabled !== false} onChange={(e) => setEditDraft({ ...editDraft, enabled: e.target.checked })} /></td>
                                        <td className="text-right">
                                            <button onClick={() => saveEdit(it.id)} className="prc-btn-primary !px-2 !py-1 text-xs mr-1">保存</button>
                                            <button onClick={() => setEditingId(null)} className="prc-btn-ghost !px-2 !py-1 text-xs">キャンセル</button>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td><span className="inline-block w-5 h-5 rounded-sm border border-[var(--prc-border)]" style={{ backgroundColor: it.color || "transparent" }} aria-hidden /></td>
                                        <td className="font-mono text-xs">{it.value}{it.system && <span className="ml-2 inline-block px-1 py-0 border border-[var(--prc-border)] rounded-sm text-[9px] font-mono uppercase text-[var(--prc-muted)]">system</span>}</td>
                                        <td className="font-semibold">{it.label}</td>
                                        <td className="num">{it.sort_order}</td>
                                        <td className="text-center">
                                            <button onClick={() => toggleEnabled(it)} className={`px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider ${it.enabled ? "border-[var(--prc-green)]/40 bg-[var(--prc-green)]/10 text-[var(--prc-green)]" : "border-[var(--prc-border)] text-[var(--prc-muted)]"}`}>
                                                {it.enabled ? "ON" : "OFF"}
                                            </button>
                                        </td>
                                        <td className="text-right">
                                            <button onClick={() => beginEdit(it)} className="prc-btn-ghost !px-2 !py-1 text-xs mr-1">編集</button>
                                            {!it.system && (
                                                <button onClick={() => remove(it)} className="prc-btn-ghost !px-2 !py-1 text-xs text-rose-300">削除</button>
                                            )}
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="border-t border-[var(--prc-border)] mt-4 pt-4">
                <p className="prc-label-overline mb-2">新規追加</p>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 text-xs">
                    <div>
                        <label className="prc-label-overline block mb-1">色</label>
                        <input type="color" className="w-full h-9 rounded-sm border border-[var(--prc-border)] bg-transparent" value={newItem.color} onChange={(e) => setNewItem({ ...newItem, color: e.target.value })} />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">コード *</label>
                        <input className="prc-input" value={newItem.value} onChange={(e) => setNewItem({ ...newItem, value: e.target.value })} data-testid={`tx-new-value-${category}`} />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">表示名 *</label>
                        <input className="prc-input" value={newItem.label} onChange={(e) => setNewItem({ ...newItem, label: e.target.value })} data-testid={`tx-new-label-${category}`} />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">並び順</label>
                        <input type="number" className="prc-input" value={newItem.sort_order} onChange={(e) => setNewItem({ ...newItem, sort_order: parseInt(e.target.value) || 0 })} />
                    </div>
                    <div className="flex items-end">
                        <button onClick={create} className="prc-btn-primary w-full" data-testid={`tx-new-submit-${category}`}>追加</button>
                    </div>
                </div>
                <p className="text-[10px] text-[var(--prc-muted)] mt-2">※ コードは英小文字・数字・アンダースコアのみ（既存レコードとの互換のため変更不可）</p>
            </div>
        </div>
    );
}

function OperationKindsCard() {
    const [kinds, setKinds] = useState([]);
    const [newK, setNewK] = useState({ value: "", label: "", color: "#38bdf8", sort_order: 50 });
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState({});

    const load = async () => {
        try {
            const { data } = await api.get("/operation-kinds");
            setKinds(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "読込失敗"); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        const v = newK.value.trim().toLowerCase();
        const l = newK.label.trim();
        if (!v || !l) { toast.error("コードと表示名を入力してください"); return; }
        if (!/^[a-z0-9_]+$/.test(v)) { toast.error("コードは英小文字・数字・アンダースコアのみ使用可能です"); return; }
        try {
            await api.post("/operation-kinds", { ...newK, value: v, label: l });
            toast.success("区分を追加しました");
            setNewK({ value: "", label: "", color: "#38bdf8", sort_order: 50 });
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "追加失敗"); }
    };

    const beginEdit = (k) => {
        setEditingId(k.id);
        setEditDraft({ label: k.label, color: k.color || "", sort_order: k.sort_order, enabled: k.enabled });
    };
    const saveEdit = async (id) => {
        try {
            await api.patch(`/operation-kinds/${id}`, editDraft);
            toast.success("更新しました");
            setEditingId(null);
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "更新失敗"); }
    };
    const remove = async (k) => {
        if (!window.confirm(`区分「${k.label}」を削除しますか？`)) return;
        try {
            await api.delete(`/operation-kinds/${k.id}`);
            toast.success("削除しました");
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "削除失敗"); }
    };
    const toggleEnabled = async (k) => {
        try {
            await api.patch(`/operation-kinds/${k.id}`, { enabled: !k.enabled });
            await load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "更新失敗"); }
    };

    return (
        <div className="prc-card p-6" data-testid="op-kinds-card">
            <p className="prc-label-overline mb-3">運行区分マスタ</p>
            <p className="text-xs text-[var(--prc-muted)] mb-3">
                運行記録ダイアログの <code className="text-[10px]">区分</code> ドロップダウンに表示される選択肢を管理します。
                <span className="ml-1">SYSTEM 印の既定区分は削除不可（無効化のみ可）、使用中の区分も削除不可です。</span>
            </p>

            {/* List */}
            <div className="overflow-x-auto">
                <table className="prc-table text-sm" data-testid="op-kinds-table">
                    <thead>
                        <tr>
                            <th className="text-left w-12">色</th>
                            <th className="text-left">コード</th>
                            <th className="text-left">表示名</th>
                            <th className="text-right">並び順</th>
                            <th className="text-center">有効</th>
                            <th className="text-right">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {kinds.map((k) => (
                            <tr key={k.id} data-testid={`op-kind-row-${k.value}`} className={k.enabled ? "" : "opacity-50"}>
                                {editingId === k.id ? (
                                    <>
                                        <td><input type="color" className="w-8 h-8 rounded-sm border border-[var(--prc-border)] bg-transparent" value={editDraft.color || "#888888"} onChange={(e) => setEditDraft({ ...editDraft, color: e.target.value })} /></td>
                                        <td className="font-mono text-xs">{k.value}{k.system && <span className="ml-2 inline-block px-1 py-0 border border-[var(--prc-border)] rounded-sm text-[9px] font-mono uppercase text-[var(--prc-muted)]">system</span>}</td>
                                        <td><input className="prc-input !py-1" value={editDraft.label} onChange={(e) => setEditDraft({ ...editDraft, label: e.target.value })} data-testid={`op-kind-edit-label-${k.value}`} /></td>
                                        <td className="num"><input type="number" className="prc-input !py-1 !w-20 text-right" value={editDraft.sort_order ?? 0} onChange={(e) => setEditDraft({ ...editDraft, sort_order: parseInt(e.target.value) || 0 })} /></td>
                                        <td className="text-center"><input type="checkbox" checked={editDraft.enabled !== false} onChange={(e) => setEditDraft({ ...editDraft, enabled: e.target.checked })} /></td>
                                        <td className="text-right">
                                            <button onClick={() => saveEdit(k.id)} className="prc-btn-primary !px-2 !py-1 text-xs mr-1" data-testid={`op-kind-save-${k.value}`}>保存</button>
                                            <button onClick={() => setEditingId(null)} className="prc-btn-ghost !px-2 !py-1 text-xs">キャンセル</button>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td><span className="inline-block w-5 h-5 rounded-sm border border-[var(--prc-border)]" style={{ backgroundColor: k.color || "transparent" }} aria-hidden /></td>
                                        <td className="font-mono text-xs">{k.value}{k.system && <span className="ml-2 inline-block px-1 py-0 border border-[var(--prc-border)] rounded-sm text-[9px] font-mono uppercase text-[var(--prc-muted)]">system</span>}</td>
                                        <td className="font-semibold">{k.label}</td>
                                        <td className="num">{k.sort_order}</td>
                                        <td className="text-center">
                                            <button onClick={() => toggleEnabled(k)} className={`px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider ${k.enabled ? "border-[var(--prc-green)]/40 bg-[var(--prc-green)]/10 text-[var(--prc-green)]" : "border-[var(--prc-border)] text-[var(--prc-muted)]"}`} data-testid={`op-kind-toggle-${k.value}`}>
                                                {k.enabled ? "ON" : "OFF"}
                                            </button>
                                        </td>
                                        <td className="text-right">
                                            <button onClick={() => beginEdit(k)} className="prc-btn-ghost !px-2 !py-1 text-xs mr-1" data-testid={`op-kind-edit-${k.value}`}>編集</button>
                                            {!k.system && (
                                                <button onClick={() => remove(k)} className="prc-btn-ghost !px-2 !py-1 text-xs text-rose-300" data-testid={`op-kind-delete-${k.value}`}>削除</button>
                                            )}
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Create form */}
            <div className="border-t border-[var(--prc-border)] mt-4 pt-4">
                <p className="prc-label-overline mb-2">新しい区分を追加</p>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 text-xs">
                    <div>
                        <label className="prc-label-overline block mb-1">色</label>
                        <input type="color" className="w-full h-9 rounded-sm border border-[var(--prc-border)] bg-transparent" value={newK.color} onChange={(e) => setNewK({ ...newK, color: e.target.value })} data-testid="op-kind-new-color" />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">コード *</label>
                        <input className="prc-input" placeholder="例: pickup" value={newK.value} onChange={(e) => setNewK({ ...newK, value: e.target.value })} data-testid="op-kind-new-value" />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">表示名 *</label>
                        <input className="prc-input" placeholder="例: 送迎" value={newK.label} onChange={(e) => setNewK({ ...newK, label: e.target.value })} data-testid="op-kind-new-label" />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">並び順</label>
                        <input type="number" className="prc-input" value={newK.sort_order} onChange={(e) => setNewK({ ...newK, sort_order: parseInt(e.target.value) || 0 })} data-testid="op-kind-new-order" />
                    </div>
                    <div className="flex items-end">
                        <button onClick={create} className="prc-btn-primary w-full" data-testid="op-kind-new-submit">追加</button>
                    </div>
                </div>
                <p className="text-[10px] text-[var(--prc-muted)] mt-2">※ コードは英小文字・数字・アンダースコアのみ（既存運行記録との互換のため変更不可）</p>
            </div>
        </div>
    );
}

function WhiteLabelCard({ company, onSaved }) {
    const { loadBranding } = useAuth();
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/company/branding").then((r) => setData(r.data)).catch(() => {});
    }, [company]);

    if (!data) return <div className="prc-card p-6 text-[var(--prc-muted)]">読込中…</div>;
    const available = data.white_label_available;
    const wl = data.white_label || {};

    const set = (k, v) => setData({ ...data, white_label: { ...wl, [k]: v } });

    const onLogo = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 2_000_000) { toast.error("ロゴは 2MB 以下にしてください"); return; }
        const r = new FileReader();
        r.onload = () => set("logo_data_url", r.result);
        r.readAsDataURL(f);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/company/white-label", data.white_label);
            toast.success("ホワイトラベル設定を保存しました。再読み込みで反映されます。");
            await loadBranding();
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="prc-card p-6 space-y-4" data-testid="white-label-card">
            <div className="flex items-center justify-between">
                <div>
                    <p className="prc-label-overline !text-[var(--prc-green)]">// WHITE LABEL</p>
                    <h3 className="text-lg font-bold text-[var(--prc-ink-2)] mt-0.5">ブランドカスタマイズ</h3>
                    <p className="text-xs text-[var(--prc-muted)] mt-1">自社ブランド（ロゴ、名称、カラー）でアプリを表示できます。</p>
                </div>
                {!available && (
                    <span className="prc-badge warn text-[10px]" data-testid="wl-upgrade-required">
                        Business / Enterprise プランで利用可能
                    </span>
                )}
            </div>

            {!available ? (
                <div className="p-4 border border-[var(--prc-amber)] bg-[rgba(255,181,71,0.05)] rounded-sm text-sm">
                    現在のプラン（<strong className="font-mono uppercase">{data.plan || "free"}</strong>）ではホワイトラベルをご利用いただけません。
                    <Link to="/billing" className="ml-2 text-[var(--prc-accent)] underline">プランを確認 →</Link>
                </div>
            ) : (
                <>
                    <label className="flex items-center gap-3 p-3 border border-[var(--prc-border-strong)] rounded-sm cursor-pointer">
                        <input type="checkbox" checked={!!wl.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="wl-enable" />
                        <div>
                            <p className="font-semibold text-[var(--prc-ink-2)]">ホワイトラベルを有効化</p>
                            <p className="text-xs text-[var(--prc-muted)]">サイドバーとメールテンプレートで自社ブランドが使用されます。</p>
                        </div>
                    </label>

                    <div className="grid md:grid-cols-3 gap-4">
                        <div>
                            <label className="prc-label-overline block mb-1">ロゴ（24x24 px 推奨）</label>
                            <div className="border border-dashed border-[var(--prc-border-strong)] p-4 rounded-sm bg-[var(--prc-surface-2)] text-center">
                                {wl.logo_data_url ? (
                                    <img src={wl.logo_data_url} alt="tenant-logo" className="h-12 mx-auto" />
                                ) : <p className="text-xs text-[var(--prc-muted)] py-3">未設定</p>}
                                <input type="file" accept="image/*" onChange={onLogo} className="text-xs mt-2 w-full" data-testid="wl-logo-upload" />
                                {wl.logo_data_url && (
                                    <button className="prc-btn-ghost text-[10px] mt-1" onClick={() => set("logo_data_url", null)}>削除</button>
                                )}
                            </div>
                        </div>
                        <F label="ブランド名" value={wl.brand_name || ""} onChange={(v) => set("brand_name", v)} testid="wl-brand-name" />
                        <F label="ブランドサブ" value={wl.brand_sub || ""} onChange={(v) => set("brand_sub", v)} testid="wl-brand-sub" />
                        <div className="md:col-span-2">
                            <label className="prc-label-overline block mb-1">キャッチコピー</label>
                            <input className="prc-input" value={wl.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="wl-tagline" />
                        </div>
                        <div>
                            <label className="prc-label-overline block mb-1">アクセントカラー</label>
                            <div className="flex items-center gap-2">
                                <input type="color" value={wl.accent_color || "#00ff9c"} onChange={(e) => set("accent_color", e.target.value)} className="h-10 w-16 bg-transparent border border-[var(--prc-border-strong)] rounded-sm cursor-pointer" data-testid="wl-accent-color" />
                                <input className="prc-input flex-1 font-mono" value={wl.accent_color || "#00ff9c"} onChange={(e) => set("accent_color", e.target.value)} />
                            </div>
                        </div>
                        <div className="md:col-span-3">
                            <label className="prc-label-overline block mb-1">フッター注釈</label>
                            <input className="prc-input" value={wl.footer_note || ""} onChange={(e) => set("footer_note", e.target.value)} data-testid="wl-footer" />
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-xs text-[var(--prc-muted)] cursor-pointer">
                        <input type="checkbox" checked={!!wl.hide_powered_by} onChange={(e) => set("hide_powered_by", e.target.checked)} data-testid="wl-hide-powered" />
                        "Powered by PRC CMC" を非表示（Enterprise プランのみ実質的に有効）
                    </label>

                    <div className="flex justify-end">
                        <button className="prc-btn-primary" onClick={save} disabled={saving} data-testid="wl-save">
                            {saving ? "保存中…" : "ホワイトラベル保存"}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

function F({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="prc-label-overline block mb-1">{label}</label>
            <input className="prc-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}
function Row({ k, v, mono }) {
    return (
        <div className="flex">
            <dt className="w-40 prc-label-overline">{k}</dt>
            <dd className={`flex-1 ${mono ? "font-mono text-xs" : ""}`}>{v}</dd>
        </div>
    );
}
